import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  ShieldCheckIcon,
  TruckIcon,
  ArrowPathIcon,
  SparklesIcon,
  FireIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { productsApi, homepageApi } from '@/services/api';
import { Category, Product } from '@/types';
import { ROUTES } from '@/utils/constants';
import { getPrimaryImage, getImageUrl } from '@/utils/imageUtils';
import { cn } from '@/utils/cn';
import { ProductCarousel, type Product as CarouselProduct } from '@/components/product/ProductCarousel';
import { CountdownTimer } from '@/components/ui/CountdownTimer';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { fadeInUp, staggerContainer, staggerItem } from '@/utils/animations';
import { RootState, AppDispatch } from '@/store';
import { fetchCategoryTree } from '@/store/slices/categoriesSlice';

// Hero Carousel Component
const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slides = [
    {
      title: 'Sustainable Shopping Starts Here',
      subtitle: 'Discover premium products with a conscience',
      cta: 'Shop Recycled',
      ctaLink: '/products?product_type=recycled',
      bg: 'from-primary-600 via-primary-500 to-accent-500',
      image: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=1200',
    },
    {
      title: 'New Arrivals Every Day',
      subtitle: 'Fresh finds at unbeatable prices',
      cta: 'Explore New',
      ctaLink: '/products?product_type=brand_new',
      bg: 'from-secondary-600 via-secondary-500 to-primary-500',
      image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1200',
    },
    {
      title: 'Save Big on Top Brands',
      subtitle: 'Up to 70% off on selected items',
      cta: 'Shop Deals',
      ctaLink: '/products',
      bg: 'from-accent-600 via-accent-500 to-primary-500',
      image: 'https://images.unsplash.com/photo-1607082349566-187342175e2f?w=1200',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden bg-dark-900">
      {slides.map((slide, index) => (
        <motion.div
          key={index}
          className={cn(
            'absolute inset-0',
            currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: currentSlide === index ? 1 : 0 }}
          transition={{ duration: 0.7 }}
        >
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className={cn('absolute inset-0 bg-gradient-to-r', slide.bg, 'opacity-90')} />
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <motion.div
                className="max-w-2xl"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: currentSlide === index ? 0 : 50, opacity: currentSlide === index ? 1 : 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                  {slide.title}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8">
                  {slide.subtitle}
                </p>
                <Button
                  size="xl"
                  variant="secondary"
                  className="bg-white hover:bg-gray-100 text-primary-600 shadow-xl"
                  onClick={() => navigate(slide.ctaLink)}
                  rightIcon={<ArrowRightIcon className="h-5 w-5" />}
                >
                  {slide.cta}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      ))}

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={cn(
              'transition-all duration-300 rounded-full',
              currentSlide === index
                ? 'w-12 h-3 bg-white'
                : 'w-3 h-3 bg-white/50 hover:bg-white/75'
            )}
          />
        ))}
      </div>
    </section>
  );
};

// Category Card Component
const CategoryCard = ({ category }: { category: Category }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      variants={staggerItem}
      whileHover={{ y: -4, scale: 1.02 }}
      className={cn(
        'relative flex flex-col overflow-hidden',
        'bg-white dark:bg-dark-800',
        'border border-gray-200 dark:border-dark-700 rounded-xl',
        'hover:border-primary-300 dark:hover:border-primary-700',
        'hover:shadow-lg transition-all duration-200',
        'group cursor-pointer'
      )}
      onClick={() => navigate(`/category/${category.slug}`)}
    >
      {/* Category Image */}
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-dark-700 dark:to-dark-600">
        {category.image_url ? (
          <img
            src={getImageUrl(category.image_url)}
            alt={category.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              // Show fallback letter if image fails to load
              target.style.display = 'none';
              if (target.nextSibling) {
                (target.nextSibling as HTMLElement).style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div
          className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30"
          style={{ display: category.image_url ? 'none' : 'flex' }}
        >
          <span className="text-5xl font-bold text-primary-600 dark:text-primary-400">
            {category.name.charAt(0)}
          </span>
        </div>
      </div>

      {/* Category Name */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white text-center group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
          {category.name}
        </h3>
      </div>
    </motion.div>
  );
};

// Trust Badge Component
const TrustBadge = ({ icon: Icon, title, description }: { icon: any; title: string; description: string }) => (
  <motion.div
    variants={staggerItem}
    className="flex items-start gap-4 p-6 bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700"
  >
    <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
      <Icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
    </div>
    <div>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{title}</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  </motion.div>
);

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { categoryTree } = useSelector((state: RootState) => state.categories);
  const [loading, setLoading] = useState(true);
  const [featuredProducts, setFeaturedProducts] = useState<CarouselProduct[]>([]);
  const [trendingProducts, setTrendingProducts] = useState<CarouselProduct[]>([]);
  const [recycledProducts, setRecycledProducts] = useState<CarouselProduct[]>([]);
  const [brandNewProducts, setBrandNewProducts] = useState<CarouselProduct[]>([]);
  const [dealsProducts, setDealsProducts] = useState<CarouselProduct[]>([]);

  // Calculate deal end time (24 hours from now)
  const dealEndTime = new Date(Date.now() + 24 * 60 * 60 * 1000);

  // Fetch category tree on mount
  useEffect(() => {
    if (categoryTree.length === 0) {
      dispatch(fetchCategoryTree());
    }
  }, [dispatch, categoryTree.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {

        // Set loading to false early
        setLoading(false);

        // Fetch products
        const convertToCarouselProduct = (product: Product): CarouselProduct => ({
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: product.original_price && product.original_price > product.price
            ? product.original_price
            : undefined,
          image: getPrimaryImage(product.images),
          rating: 4.5, // Mock rating
          reviewCount: Math.floor(Math.random() * 500) + 50,
          category: product.category_name,
          isNew: product.product_type === 'brand_new',
          isRecycled: product.product_type === 'recycled',
          discount: product.original_price && product.original_price > product.price
            ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
            : undefined,
          inStock: product.quantity_available > 0,
        });

        // Fetch featured products
        try {
          const featuredResponse = await productsApi.getFeatured({ skip: 0, limit: 12 });
          const data = featuredResponse.data;
          const products = Array.isArray(data) ? data : (data?.items || []);
          setFeaturedProducts(products.map(convertToCarouselProduct));
          // Use featured products as deals for now
          setDealsProducts(products.slice(0, 6).map(convertToCarouselProduct));
        } catch (err) {
          console.error('Error fetching featured products:', err);
        }

        // Fetch trending products
        try {
          const trendingResponse = await productsApi.getAll({ skip: 0, limit: 12 });
          const data = trendingResponse.data;
          const products = Array.isArray(data) ? data : (data?.items || []);
          setTrendingProducts(products.map(convertToCarouselProduct));
        } catch (err) {
          console.error('Error fetching trending products:', err);
        }

        // Fetch recycled products
        try {
          const recycledResponse = await productsApi.getByType('recycled', { skip: 0, limit: 12 });
          const data = recycledResponse.data;
          const products = Array.isArray(data) ? data : (data?.items || []);
          setRecycledProducts(products.map(convertToCarouselProduct));
        } catch (err) {
          console.error('Error fetching recycled products:', err);
        }

        // Fetch brand new products
        try {
          const brandNewResponse = await productsApi.getByType('brand_new', { skip: 0, limit: 12 });
          const data = brandNewResponse.data;
          const products = Array.isArray(data) ? data : (data?.items || []);
          setBrandNewProducts(products.map(convertToCarouselProduct));
        } catch (err) {
          console.error('Error fetching brand new products:', err);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading amazing products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Hero Carousel */}
      <HeroCarousel />

      {/* Trust Badges Bar */}
      <motion.section
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="py-8 bg-white dark:bg-dark-800 border-y border-gray-200 dark:border-dark-700"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <TrustBadge
              icon={ShieldCheckIcon}
              title="Secure Payment"
              description="100% protected transactions"
            />
            <TrustBadge
              icon={TruckIcon}
              title="Fast Delivery"
              description="Free shipping on orders over $50"
            />
            <TrustBadge
              icon={ArrowPathIcon}
              title="Easy Returns"
              description="30-day return policy"
            />
            <TrustBadge
              icon={SparklesIcon}
              title="Quality Guaranteed"
              description="Verified products only"
            />
          </div>
        </div>
      </motion.section>

      {/* Deals of the Day */}
      {dealsProducts.length > 0 && (
        <section className="py-12 bg-gradient-to-br from-error-50 to-accent-50 dark:from-error-900/10 dark:to-accent-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <FireIcon className="h-8 w-8 text-error-500" />
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                    Deals of the Day
                  </h2>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Limited time offers - Don't miss out!
                </p>
              </div>
              <CountdownTimer
                targetDate={dealEndTime}
                variant="cards"
                className="self-start md:self-center"
              />
            </div>
            <ProductCarousel
              products={dealsProducts}
              showNavigation
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 4 }}
            />
          </div>
        </section>
      )}

      {/* Shop by Category */}
      {categoryTree.length > 0 && (
        <motion.section
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="py-12 bg-white dark:bg-dark-850"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                  Shop by Category
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Find exactly what you're looking for
                </p>
              </div>
              <Link
                to={ROUTES.PRODUCTS}
                className="hidden md:flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold hover:gap-3 transition-all"
              >
                View All
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
            </div>
            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"
            >
              {categoryTree.slice(0, 12).map((category) => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-12 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ProductCarousel
              title="Featured Products"
              subtitle="Handpicked just for you"
              products={featuredProducts}
              showNavigation
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 4 }}
            />
            <div className="mt-6 text-center">
              <Button
                size="lg"
                onClick={() => navigate(ROUTES.PRODUCTS)}
                rightIcon={<ArrowRightIcon className="h-5 w-5" />}
                className="bg-sky-500 hover:bg-sky-600 text-white border-sky-500 hover:border-sky-600"
              >
                View All Products
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Sustainable Shopping Section */}
      {recycledProducts.length > 0 && (
        <section className="py-12 bg-gradient-to-br from-success-50 via-primary-50 to-success-50 dark:from-success-900/10 dark:via-primary-900/10 dark:to-success-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <Badge variant="success" className="mb-4 text-base px-4 py-2">
                Eco-Friendly
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Sustainable Shopping
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Make a difference with every purchase. Our recycled products help reduce waste
                and protect our planet for future generations.
              </p>
            </div>
            <ProductCarousel
              products={recycledProducts}
              showNavigation
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 4 }}
            />
          </div>
        </section>
      )}

      {/* Trending Now */}
      {trendingProducts.length > 0 && (
        <section className="py-12 bg-white dark:bg-dark-850">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ProductCarousel
              title="Trending Now"
              subtitle="What's hot right now"
              products={trendingProducts}
              showNavigation
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 4 }}
            />
          </div>
        </section>
      )}

      {/* New Arrivals */}
      {brandNewProducts.length > 0 && (
        <section className="py-12 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ProductCarousel
              title="New Arrivals"
              subtitle="Fresh picks just added"
              products={brandNewProducts}
              showNavigation
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 4 }}
            />
          </div>
        </section>
      )}

      {/* Promotional Banners */}
      <section className="py-12 bg-white dark:bg-dark-850">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className={cn(
                'relative p-8 rounded-2xl overflow-hidden',
                'bg-gradient-to-br from-primary-500 to-primary-700',
                'text-white cursor-pointer group'
              )}
              onClick={() => navigate('/products?category=electronics')}
            >
              <div className="relative z-10">
                <TagIcon className="h-10 w-10 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Electronics Sale</h3>
                <p className="mb-4 opacity-90">Up to 50% off on gadgets</p>
                <span className="inline-flex items-center gap-2 font-semibold group-hover:gap-3 transition-all">
                  Shop Now
                  <ArrowRightIcon className="h-5 w-5" />
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-white/10" />
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className={cn(
                'relative p-8 rounded-2xl overflow-hidden',
                'bg-gradient-to-br from-secondary-500 to-secondary-700',
                'text-white cursor-pointer group'
              )}
              onClick={() => navigate('/products?category=fashion')}
            >
              <div className="relative z-10">
                <SparklesIcon className="h-10 w-10 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Fashion Finds</h3>
                <p className="mb-4 opacity-90">New styles added weekly</p>
                <span className="inline-flex items-center gap-2 font-semibold group-hover:gap-3 transition-all">
                  Explore
                  <ArrowRightIcon className="h-5 w-5" />
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-white/10" />
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className={cn(
                'relative p-8 rounded-2xl overflow-hidden',
                'bg-gradient-to-br from-accent-500 to-accent-700',
                'text-dark-900 cursor-pointer group'
              )}
              onClick={() => navigate('/dashboard/seller/shop/create')}
            >
              <div className="relative z-10">
                <FireIcon className="h-10 w-10 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Become a Seller</h3>
                <p className="mb-4 opacity-90">Start your business today</p>
                <span className="inline-flex items-center gap-2 font-semibold group-hover:gap-3 transition-all">
                  Get Started
                  <ArrowRightIcon className="h-5 w-5" />
                </span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-white/20" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
